import { createWithEqualityFn } from 'zustand/traditional';
import { ScholarshipStatus, UserPlanning } from '@common-types';

import { SortByValues } from 'pages/Scholarships/components/SortBy';

import useEducationPlannerStore from '@store/EducationPlannerStore';
import useNotificationStore from '@store/NotificationStore';

import { INITIAL_FILTERS } from '@constants/applyingScholarships';

import { baseService } from '@services/base.service';

interface PageFilters {
  tagValue: ScholarshipStatus | string;
  tagColor: string;
  sortByValues: SortByValues;
}

export interface ApplyingScholarships {
  pageFilters: PageFilters;

  applyScholarship: (id: number) => void;
  changeApplyStatus: (id: number, status: ScholarshipStatus) => void;
  setFilter: (filter: Partial<PageFilters>) => void;
}

const initialState = {
  pageFilters: INITIAL_FILTERS as PageFilters,
};

export const useApplyingScholarshipsStore = createWithEqualityFn<ApplyingScholarships>()(
  (set, get) => ({
    ...initialState,

    setFilter: (filter: Partial<PageFilters>) => {
      set({
        pageFilters: { ...get().pageFilters, ...filter },
      });
    },

    applyScholarship: async (id: number) => {
      const result = await baseService.postAsync<UserPlanning, { id: number; status: string }>(
        '/user/applying-scholarship',
        {
          id,
          status: ScholarshipStatus.IN_PROGRESS,
        },
      );

      useEducationPlannerStore.getState().setUserPlanning(result);

      useNotificationStore.getState().notify({
        message: 'Scholarship has been sent to your Applied list.',
        severity: 'info',
      });
    },

    changeApplyStatus: async (id: number, status: ScholarshipStatus) => {
      const result = await baseService.patchAsync<UserPlanning, unknown>(`/user/applying-scholarship/${id}`, {
        status,
      });

      useEducationPlannerStore.getState().setUserPlanning(result);

      const message =
        status === ScholarshipStatus.CHANGED
          ? 'Scholarship has been deleted from your application list'
          : `Scholarship status has been updated with ${status}`;

      useNotificationStore.getState().notify({
        message,
        severity: 'info',
      });
    },
  }),
  Object.is,
);
